import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Loader2, MessageCircleHeart } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimoni() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [rating, setRating] = useState(5);
    const [form, setForm] = useState({ name: "", text: "" });

    const load = async () => {
        try {
            const res = await axios.get(`${API}/testimonials`);
            setList(res.data);
        } catch {
            /* pertama kali mungkin masih kosong */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/testimonials`, { ...form, rating });
            toast.success("Terima kasih, ulasanmu sudah masuk!");
            setForm({ name: "", text: "" });
            setRating(5);
            load();
        } catch {
            toast.error("Gagal mengirim ulasan. Coba lagi ya.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="pt-24 lg:pt-32" data-testid="testimoni-page">
            <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <span className="eyebrow">Kata mereka</span>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-4 font-display text-5xl font-extrabold tracking-tighter text-ink sm:text-6xl lg:text-7xl">
                        Testimoni.
                    </h1>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-5 max-w-xl leading-relaxed text-ink-muted">
                        Pernah mampir atau pesan? Tulis ulasanmu di sini —
                        semuanya kami baca untuk bikin Yuhuku makin enak.
                    </p>
                </Reveal>
            </header>

            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-20">
                <div className="lg:col-span-7">
                    <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink">
                        Ulasan pelanggan
                    </h2>
                    {loading ? (
                        <p className="mt-6 flex items-center gap-2 text-sm text-ink-muted" data-testid="testimoni-loading">
                            <Loader2 className="h-4 w-4 animate-spin" /> Memuat ulasan...
                        </p>
                    ) : list.length === 0 ? (
                        <div
                            data-testid="testimoni-empty"
                            className="mt-6 rounded-3xl border border-dashed border-ink/15 px-6 py-12 text-center"
                        >
                            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent">
                                <MessageCircleHeart className="h-5 w-5" />
                            </span>
                            <p className="mt-4 font-semibold text-ink">
                                Belum ada ulasan — jadilah yang pertama!
                            </p>
                            <p className="mt-1 text-sm text-ink-muted">
                                Isi formulir di samping, ulasanmu langsung
                                tampil di sini.
                            </p>
                        </div>
                    ) : (
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            {list.map((t) => (
                                <div
                                    key={t.id}
                                    data-testid="testimoni-card"
                                    className="rounded-3xl border border-ink/5 bg-white p-6 shadow-soft"
                                >
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                className={`h-4 w-4 ${
                                                    i < t.rating
                                                        ? "fill-accent text-accent"
                                                        : "text-ink/20"
                                                }`}
                                            />
                                        ))}
                                    </div>
                                    <p className="mt-3.5 leading-relaxed text-ink/85">
                                        "{t.text}"
                                    </p>
                                    <p className="mt-4 text-sm font-bold text-accent">
                                        — {t.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-5">
                    <Reveal delay={0.1}>
                        <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-soft sm:p-8">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink">
                                Tulis Ulasan
                            </h2>
                            <form
                                onSubmit={onSubmit}
                                data-testid="testimoni-form"
                                className="mt-6"
                            >
                                <label className="block">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Nama
                                    </span>
                                    <input
                                        data-testid="testimoni-input-name"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        required
                                        maxLength={80}
                                        placeholder="Namamu"
                                        className="input-clean"
                                    />
                                </label>
                                <div className="mt-5">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Rating
                                    </span>
                                    <div className="flex gap-1.5" data-testid="testimoni-rating">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                aria-label={`Beri ${i + 1} bintang`}
                                                data-testid={`testimoni-star-${i + 1}`}
                                                onClick={() => setRating(i + 1)}
                                                className="transition-transform hover:scale-110"
                                            >
                                                <Star
                                                    className={`h-7 w-7 ${
                                                        i < rating
                                                            ? "fill-accent text-accent"
                                                            : "text-ink/20"
                                                    }`}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <label className="mt-5 block">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Ulasan
                                    </span>
                                    <textarea
                                        data-testid="testimoni-input-text"
                                        value={form.text}
                                        onChange={(e) => setForm({ ...form, text: e.target.value })}
                                        required
                                        maxLength={500}
                                        rows={4}
                                        placeholder="Gimana kopinya?"
                                        className="input-clean resize-none"
                                    />
                                </label>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="testimoni-submit"
                                    className="btn-primary mt-6 w-full"
                                >
                                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {sending ? "Mengirim..." : "Kirim Ulasan"}
                                </button>
                            </form>
                        </div>
                    </Reveal>
                </div>
            </div>
        </div>
    );
}